/**
 * Acoustic Sub-step — Method of Characteristics (MOC) for pressure transients
 * (water hammer), as a linear acoustic overlay on the SIMPLE base flow.
 *
 * Physics (linearized about the SIMPLE base state, Wylie & Streeter form):
 *   ∂p'/∂t + ρ·c² · ∂u'/∂x = 0
 *   ∂u'/∂t + (1/ρ) · ∂p'/∂x = −k·u'          (k = optional linearized friction)
 *
 * Characteristic (compatibility) equations, impedance Z = ρ·c:
 *   C+ (dx/dt = +c):  d(p' + Z·u') = −Z·k·u'·dt
 *   C− (dx/dt = −c):  d(p' − Z·u') = +Z·k·u'·dt
 *
 * Discretization: MOC nodes at the pipe stations x_j = j·dx (the FV face
 * positions, j = 0..nCells), with p' and u' co-located at nodes. The scheme
 * runs at EXACTLY CFL = 1 (dt_sub = dx/c): each substep the Riemann
 * invariants R± = p' ± Z·u' shift one node along their characteristic with
 * no interpolation — zero numerical dissipation and exact propagation speed.
 * The caller may pass ANY dt: an internal acoustic clock accumulates time
 * credit and executes floor(credit/dt_sub) whole substeps, carrying the
 * remainder. The CFL bound dt_sub ≤ dx/c therefore holds by construction.
 *
 * Boundary conditions (per pipe end):
 *   'reservoir' — pressure node, p' = 0. Waves reflect with SIGN FLIP.
 *   'closed'    — velocity node, u' = uTarget − uBase (flow arrest). Waves
 *                 reflect WITHOUT sign flip (pressure doubles at the wall).
 *                 Becoming active at closeTime injects the velocity
 *                 discontinuity that generates the Joukowsky wave
 *                 ΔP = ρ·c·Δu. Before closeTime the end is absorbing.
 *   'open'      — absorbing (non-reflecting): the incoming external
 *                 invariant is zero; outgoing waves leave the domain.
 *
 * The perturbation state PERSISTS across calls (owned by the caller). Each
 * acousticStep call advances the field and applies the pressure/velocity
 * perturbation to the pipe incrementally (applied-delta bookkeeping), so
 * pipe = base + perturbation at all times. removeAcousticPerturbations()
 * strips the overlay (e.g. before a SIMPLE pass re-solves the base flow).
 *
 * API CHANGE (2026-07, fix wave W7): the previous signature
 * acousticStep(pipe, dt, nSubsteps) was a structural no-op — perturbation
 * arrays were re-zeroed every call, boundaries clamped them to zero, and no
 * source was ever injected, so dP ≡ 0 forever (audited: sudden closure of a
 * 2.55 m/s flow produced 0.00 bar vs 37.8 bar Joukowsky). It had no
 * production callers. The new state-first API replaces it; no compatibility
 * shim is provided because a shim would silently restore the broken
 * behavior (a fresh state per call IS the old bug).
 *
 * v1 modeling limitations (documented deliberately):
 *  - Pipe-uniform wave speed c and impedance Z (means over cells, frozen at
 *    state creation). Fine for liquid lines (c varies <1%); NOT valid for
 *    two-phase pipes — a warning is emitted when cell sound speeds deviate
 *    >20% from the mean.
 *  - Rigid pipe wall BY DEFAULT: c is the fluid sound speed (IAPWS).
 *    Elastic-wall Korteweg correction (reduces c by ~10-20% for thin steel
 *    pipe) is available via kortewegWaveSpeed() and options.waveSpeed; the
 *    production valve-closure wiring (stepAdapter) passes it whenever the
 *    pipe carries wall data (R1-A3, W7 caveat 6). Direct API use without
 *    options.waveSpeed keeps the rigid-pipe fluid c exactly as before.
 *  - Steady friction only, linearized about the base flow (optional,
 *    default OFF). Unsteady friction (Brunone/Zielke) is out of scope.
 *  - No cavitation / column separation: the linear overlay can drive the
 *    total pressure below vapor pressure; the value applied to the FV cells
 *    is floored at MIN_TOTAL_PRESSURE_PA (bookkept exactly) but the
 *    acoustic field itself stays linear.
 */

import type { PipeSegment } from '../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Fallback sound speed [m/s] — liquid water ≈25 °C — used only if cell props are unusable. */
const DEFAULT_WATER_SOUND_SPEED = 1497;

/** Fallback density [kg/m³] — liquid water ≈25 °C — used only if cell props are unusable. */
const DEFAULT_WATER_DENSITY = 998;

/** Plausible engineering range for the acoustic wave speed [m/s] (wet steam … stiff liquid). */
const MIN_WAVE_SPEED = 10;
const MAX_WAVE_SPEED = 2500;

/** Absolute-pressure floor applied to pipe cells [Pa] — keeps the FV state EOS-valid. */
const MIN_TOTAL_PRESSURE_PA = 1000;

/** Runaway guard: max acoustic substeps executed per acousticStep call. */
const MAX_SUBSTEPS_PER_CALL = 200_000;

/** Warn when cell sound speeds deviate from the pipe-uniform mean by more than this fraction. */
const NONUNIFORM_C_WARN_FRACTION = 0.2;

/** Default Darcy friction factor for the optional linearized steady-friction term. */
const DEFAULT_DARCY_FRICTION_FACTOR = 0.02;

/** Stability cap on the per-substep friction decrement k·dt_sub (explicit term). */
const MAX_FRICTION_DECREMENT = 0.5;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AcousticBoundaryKind = 'reservoir' | 'closed' | 'open';

/** Boundary specification for one pipe end. */
export interface AcousticBoundarySpec {
  kind: AcousticBoundaryKind;
  /**
   * 'closed' only — acoustic-clock time [s] at/after which the end is closed
   * (instantaneous closure, v1). Default 0 = closed from the first substep.
   * Before closeTime the end behaves as 'open' (absorbing).
   */
  closeTime?: number;
  /**
   * 'closed' only — base (pre-closure) fluid velocity at this end [m/s].
   * If omitted it is sampled from the pipe face when the boundary is set,
   * so create the state / set the boundary BEFORE zeroing the face.
   */
  uBase?: number;
  /** 'closed' only — total velocity held at the end after closure [m/s]. Default 0 (full stop). */
  uTarget?: number;
}

interface ResolvedBoundary {
  kind: AcousticBoundaryKind;
  closeTime: number;
  uBase: number;
  uTarget: number;
}

export interface AcousticOptions {
  /** Enable linearized steady friction on the characteristics (default false). */
  friction?: boolean;
  /** Darcy friction factor f used for the linearization (default 0.02). */
  frictionFactor?: number;
  /**
   * Override the pipe-uniform wave speed [m/s] (e.g. Korteweg elastic-wall
   * value). Default: mean of cell sound speeds from the EOS.
   */
  waveSpeed?: number;
}

/**
 * Persistent acoustic perturbation state for ONE pipe. Owned by the caller;
 * pass it to every acousticStep call. p/u are node arrays (nNodes = nCells+1)
 * at the pipe stations; appliedP/appliedU bookkeep what is currently added
 * onto the FV cells/faces so application is incremental and reversible.
 */
export interface AcousticState {
  /** Number of MOC nodes = pipe.nCells + 1. */
  nNodes: number;
  /** Reach length [m] = pipe.dx. */
  dx: number;
  /** Pipe-uniform wave speed [m/s] (frozen at creation). */
  c: number;
  /** Pipe-uniform density [kg/m³] (frozen at creation). */
  rho: number;
  /** Acoustic impedance Z = ρ·c [Pa·s/m]. */
  Z: number;
  /** Internal substep [s] = dx/c (CFL = 1 exactly). */
  dtSub: number;
  /** Acoustic clock [s] — total time advanced by executed substeps. */
  time: number;
  /** Unspent caller time [s], 0 ≤ timeCredit < dtSub between calls. */
  timeCredit: number;
  /** Substeps executed by the most recent acousticStep call. */
  lastSubstepCount: number;
  /** Pressure perturbation p' at nodes [Pa]. */
  p: Float64Array;
  /** Velocity perturbation u' at nodes [m/s]. */
  u: Float64Array;
  /** Perturbation currently applied to pipe cell pressures [Pa] (per cell). */
  appliedP: Float64Array;
  /** Perturbation currently applied to pipe face velocities [m/s] (per face). */
  appliedU: Float64Array;
  /** Scratch buffers (previous substep) — avoids per-substep allocation. */
  pPrev: Float64Array;
  uPrev: Float64Array;
  left: ResolvedBoundary;
  right: ResolvedBoundary;
  /** Linearized friction coefficient k = f·|u_ref|/D [1/s] (0 = frictionless). */
  frictionK: number;
}

// ---------------------------------------------------------------------------
// Pipe-mean acoustic properties (shared by state construction + Korteweg)
// ---------------------------------------------------------------------------

/** Pipe-uniform acoustic property means over the valid cells (see pipeMeanAcousticProps). */
export interface PipeAcousticMeans {
  /** Mean fluid sound speed [m/s] (DEFAULT_WATER_SOUND_SPEED when no valid cell). */
  c: number;
  /** Mean density [kg/m³] (DEFAULT_WATER_DENSITY when no valid cell). */
  rho: number;
  /** Min/max cell sound speed among valid cells (Infinity/0 when none). */
  cMin: number;
  cMax: number;
  /** Number of cells with finite, positive c AND rho. */
  nValid: number;
}

/**
 * Compute the pipe-uniform acoustic property means the overlay freezes at
 * creation: arithmetic mean of cell sound speed and density over cells with
 * finite positive values, with the liquid-water fallbacks when no cell is
 * usable.
 *
 * R1-A3 refactor note: this is a PURE EXTRACTION of the loop that previously
 * lived inline in createAcousticState — identical accumulation order, guards,
 * and fallbacks, so the frozen (c, ρ, Z, dtSub) of every acoustic state are
 * bit-identical to before. Exported so the integration layer (stepAdapter)
 * can evaluate the Korteweg elastic-wall correction from the SAME means the
 * overlay itself would freeze.
 */
export function pipeMeanAcousticProps(pipe: PipeSegment): PipeAcousticMeans {
  let cSum = 0, rhoSum = 0, cMin = Infinity, cMax = 0, nValid = 0;
  for (let i = 0; i < pipe.nCells; i++) {
    const cc = pipe.cells[i].c;
    const rr = pipe.cells[i].rho;
    if (isFinite(cc) && cc > 0 && isFinite(rr) && rr > 0) {
      cSum += cc; rhoSum += rr;
      cMin = Math.min(cMin, cc); cMax = Math.max(cMax, cc);
      nValid++;
    }
  }
  return {
    c: nValid > 0 ? cSum / nValid : DEFAULT_WATER_SOUND_SPEED,
    rho: nValid > 0 ? rhoSum / nValid : DEFAULT_WATER_DENSITY,
    cMin,
    cMax,
    nValid,
  };
}

// ---------------------------------------------------------------------------
// Korteweg elastic-wall wave speed (W7 caveat 6)
// ---------------------------------------------------------------------------

/**
 * Young's modulus of carbon-steel pipe wall [Pa].
 *
 * Source: standard handbook value for carbon steels, E ≈ 195–210 GPa
 * (e.g. Wylie & Streeter, "Fluid Transients in Systems", 1993, Table 1-2
 * lists steel E = 30×10⁶ psi ≈ 207 GPa; ASME/AISC design values 200 GPa).
 * 200 GPa is the conventional round design value and matches the carbon-steel
 * wall already assumed by the cooling factory (STEEL_RHO_CP, schedule-40 wall).
 */
export const STEEL_YOUNGS_MODULUS_PA = 200e9;

/**
 * Korteweg elastic-wall acoustic wave speed for a thin-walled pipe:
 *
 *     c_K = c / √(1 + (K/E)·(D/e))
 *
 * where c is the fluid (rigid-pipe) sound speed, K = ρ·c² the fluid's
 * isentropic bulk modulus, E the wall Young's modulus, D the internal
 * diameter and e the wall thickness.
 *
 * Physics + sources:
 *  - Korteweg (1878); Wylie & Streeter, "Fluid Transients in Systems" (1993),
 *    Eq. 1-22 with the pipe-restraint factor c₁ = 1 (thin-walled pipe,
 *    unrestrained form). Anchoring reduces the correction by ≲ ν² effects
 *    (a few %) — omitting c₁ is the standard first-order treatment and keeps
 *    the correction slightly conservative (lower c_K, longer 2L/c).
 *  - K = ρ·c² is the exact isentropic bulk modulus identity (c = √(K_s/ρ)),
 *    so K comes from the SAME fluid backend (IAPWS) state that produced
 *    ρ and c — no separate property path is introduced.
 *  - Representative magnitude: DN300 schedule-40 water line
 *    (D = 0.3 m, e = 10.9 mm, ρ ≈ 998 kg/m³, c ≈ 1482 m/s → K ≈ 2.19 GPa):
 *    (K/E)·(D/e) = 0.01096 × 27.52 ≈ 0.302 → c_K ≈ c/1.141 ≈ 1299 m/s,
 *    a 12.3% reduction — inside the 10–20% band quoted for thin steel pipe.
 *
 * Validity: thin-wall assumption D/e ≳ 10 (schedule-40 lines satisfy this for
 * DN50+); liquid lines only, same as the acoustic overlay itself.
 *
 * @param cFluid        Rigid-pipe fluid sound speed [m/s] (> 0)
 * @param rho           Fluid density [kg/m³] (> 0)
 * @param diameter      Pipe internal diameter [m] (> 0)
 * @param wallThickness Pipe wall thickness [m] (> 0)
 * @param youngsModulus Wall Young's modulus [Pa] (> 0; default steel 200 GPa)
 * @returns Korteweg-corrected wave speed [m/s] (always < cFluid)
 * @throws RangeError on non-finite or non-positive inputs
 */
export function kortewegWaveSpeed(
  cFluid: number,
  rho: number,
  diameter: number,
  wallThickness: number,
  youngsModulus: number = STEEL_YOUNGS_MODULUS_PA,
): number {
  if (!(isFinite(cFluid) && cFluid > 0)) {
    throw new RangeError(`kortewegWaveSpeed: cFluid must be > 0 (got ${cFluid})`);
  }
  if (!(isFinite(rho) && rho > 0)) {
    throw new RangeError(`kortewegWaveSpeed: rho must be > 0 (got ${rho})`);
  }
  if (!(isFinite(diameter) && diameter > 0)) {
    throw new RangeError(`kortewegWaveSpeed: diameter must be > 0 (got ${diameter})`);
  }
  if (!(isFinite(wallThickness) && wallThickness > 0)) {
    throw new RangeError(`kortewegWaveSpeed: wallThickness must be > 0 (got ${wallThickness})`);
  }
  if (!(isFinite(youngsModulus) && youngsModulus > 0)) {
    throw new RangeError(`kortewegWaveSpeed: youngsModulus must be > 0 (got ${youngsModulus})`);
  }
  const K = rho * cFluid * cFluid; // isentropic bulk modulus K_s = ρ·c² [Pa]
  return cFluid / Math.sqrt(1 + (K / youngsModulus) * (diameter / wallThickness));
}

// ---------------------------------------------------------------------------
// State construction
// ---------------------------------------------------------------------------

const DEFAULT_BOUNDARY: AcousticBoundarySpec = { kind: 'open' };

/**
 * Create the persistent acoustic state for a pipe.
 *
 * Acoustic properties (c, ρ, Z) are frozen from the pipe's CURRENT cell
 * state; base velocities for 'closed' ends are sampled from the pipe faces
 * NOW — so create the state while the pre-event base flow is still intact.
 *
 * @param pipe   The pipe segment (nCells ≥ 2 required)
 * @param bounds Boundary specs per end (default: both 'open' / absorbing)
 * @param opts   Friction / wave-speed options
 */
export function createAcousticState(
  pipe: PipeSegment,
  bounds?: { left?: AcousticBoundarySpec; right?: AcousticBoundarySpec },
  opts?: AcousticOptions,
): AcousticState {
  const { nCells, dx } = pipe;
  if (nCells < 2) {
    throw new RangeError(`acoustic state requires nCells >= 2 (got ${nCells})`);
  }
  if (!(dx > 0) || !isFinite(dx)) {
    throw new RangeError(`acoustic state requires dx > 0 (got ${dx})`);
  }

  // Pipe-uniform acoustic properties: mean over cells, with sanity fallbacks.
  // (R1-A3: loop extracted verbatim into pipeMeanAcousticProps — same order,
  // guards, and fallbacks, so frozen c/ρ/Z/dtSub are bit-identical to before.)
  const means = pipeMeanAcousticProps(pipe);
  let c = means.c;
  const rho = means.rho;

  if (opts?.waveSpeed !== undefined) {
    if (!isFinite(opts.waveSpeed) || opts.waveSpeed <= 0) {
      throw new RangeError(`options.waveSpeed must be a positive number (got ${opts.waveSpeed})`);
    }
    c = opts.waveSpeed;
  } else if (means.nValid > 0 && means.cMax - means.cMin > NONUNIFORM_C_WARN_FRACTION * c) {
    // v1 assumes a near-uniform wave speed along the pipe (liquid lines).
    console.warn(
      `[acousticSubstep] pipe ${pipe.id}: cell sound speeds vary ${means.cMin.toFixed(0)}-${means.cMax.toFixed(0)} m/s; ` +
      `v1 uses the uniform mean ${c.toFixed(0)} m/s — two-phase pipes are not supported by the acoustic overlay.`,
    );
  }
  if (c < MIN_WAVE_SPEED || c > MAX_WAVE_SPEED) {
    throw new RangeError(
      `acoustic wave speed ${c.toFixed(1)} m/s outside plausible range [${MIN_WAVE_SPEED}, ${MAX_WAVE_SPEED}]`,
    );
  }

  const nNodes = nCells + 1;
  const state: AcousticState = {
    nNodes,
    dx,
    c,
    rho,
    Z: rho * c,
    dtSub: dx / c,
    time: 0,
    timeCredit: 0,
    lastSubstepCount: 0,
    p: new Float64Array(nNodes),
    u: new Float64Array(nNodes),
    appliedP: new Float64Array(nCells),
    appliedU: new Float64Array(nNodes),
    pPrev: new Float64Array(nNodes),
    uPrev: new Float64Array(nNodes),
    left: resolveBoundary(bounds?.left ?? DEFAULT_BOUNDARY, pipe, 'left'),
    right: resolveBoundary(bounds?.right ?? DEFAULT_BOUNDARY, pipe, 'right'),
    frictionK: 0,
  };

  if (opts?.friction) {
    const f = opts.frictionFactor ?? DEFAULT_DARCY_FRICTION_FACTOR;
    if (!isFinite(f) || f < 0 || f > 0.5) {
      throw new RangeError(`frictionFactor must be in [0, 0.5] (got ${f})`);
    }
    // Linearized steady friction: F(u) = f·u|u|/(2D) → dF/du = f·|u_ref|/D.
    let uAbsSum = 0;
    for (let j = 0; j <= nCells; j++) uAbsSum += Math.abs(pipe.faces[j].u);
    const uRef = uAbsSum / (nCells + 1);
    state.frictionK = f * uRef / pipe.diameter;
    if (state.frictionK * state.dtSub > MAX_FRICTION_DECREMENT) {
      console.warn(
        `[acousticSubstep] pipe ${pipe.id}: friction decrement k·dt=` +
        `${(state.frictionK * state.dtSub).toFixed(3)} clamped to ${MAX_FRICTION_DECREMENT} for stability.`,
      );
      state.frictionK = MAX_FRICTION_DECREMENT / state.dtSub;
    }
  }

  return state;
}

/**
 * Set/replace the boundary condition at one end (e.g. a valve-closure event
 * mid-run). For 'closed' with uBase omitted, the base velocity is sampled
 * from the pipe face NOW — call this before zeroing the face externally.
 * closeTime is on the acoustic clock (state.time); pass state.time for
 * "close now".
 */
export function setAcousticBoundary(
  state: AcousticState,
  end: 'left' | 'right',
  spec: AcousticBoundarySpec,
  pipe: PipeSegment,
): void {
  const resolved = resolveBoundary(spec, pipe, end);
  if (end === 'left') state.left = resolved;
  else state.right = resolved;
}

function resolveBoundary(
  spec: AcousticBoundarySpec,
  pipe: PipeSegment,
  end: 'left' | 'right',
): ResolvedBoundary {
  const faceIdx = end === 'left' ? 0 : pipe.nCells;
  const sampled = pipe.faces[faceIdx]?.u ?? 0;
  const uBase = spec.uBase ?? (isFinite(sampled) ? sampled : 0);
  const closeTime = spec.closeTime ?? 0;
  if (!isFinite(closeTime) || closeTime < 0) {
    throw new RangeError(`closeTime must be >= 0 (got ${closeTime})`);
  }
  return {
    kind: spec.kind,
    closeTime,
    uBase,
    uTarget: spec.uTarget ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Time advance
// ---------------------------------------------------------------------------

/**
 * Advance the acoustic perturbation field by dt and apply it to the pipe.
 *
 * Internally executes whole CFL = 1 substeps (dt_sub = dx/c exactly) on the
 * acoustic clock; leftover time < dt_sub is credited to the next call, so
 * repeated small-dt calls and one large-dt call produce identical fields.
 * After advancing, the perturbation is applied incrementally to
 * pipe.cells[].P and pipe.faces[].{u, mdot} (pipe = base + perturbation).
 *
 * @param state Persistent acoustic state from createAcousticState
 * @param pipe  The pipe segment the state was created for
 * @param dt    Caller time increment [s] (any non-negative value)
 * @returns The same state (advanced), for chaining
 */
export function acousticStep(
  state: AcousticState,
  pipe: PipeSegment,
  dt: number,
): AcousticState {
  if (!isFinite(dt) || dt < 0) {
    throw new RangeError(`acousticStep dt must be >= 0 (got ${dt})`);
  }
  if (state.nNodes !== pipe.nCells + 1) {
    throw new RangeError(
      `acoustic state (${state.nNodes} nodes) does not match pipe ${pipe.id} (${pipe.nCells} cells)`,
    );
  }

  state.timeCredit += dt;
  // Relative epsilon absorbs FP accumulation when dt is an exact multiple of dtSub.
  let nSteps = Math.floor(state.timeCredit / state.dtSub + 1e-9);
  if (nSteps > MAX_SUBSTEPS_PER_CALL) {
    console.warn(
      `[acousticSubstep] pipe ${pipe.id}: ${nSteps} substeps requested in one call; ` +
      `clamping to ${MAX_SUBSTEPS_PER_CALL} (acoustic clock will lag).`,
    );
    nSteps = MAX_SUBSTEPS_PER_CALL;
  }

  for (let k = 0; k < nSteps; k++) {
    advanceOneSubstep(state);
  }
  state.timeCredit = Math.max(0, state.timeCredit - nSteps * state.dtSub);
  state.lastSubstepCount = nSteps;

  applyPerturbationsToPipe(state, pipe);
  return state;
}

/**
 * One exact CFL = 1 MOC substep: Riemann invariants R± = p' ± Z·u' shift one
 * node along their characteristic; boundary nodes solve the single incoming
 * compatibility equation together with the boundary condition.
 */
function advanceOneSubstep(state: AcousticState): void {
  const { p, u, pPrev, uPrev, Z, nNodes, dtSub, frictionK } = state;
  const N = nNodes - 1;
  const fk = frictionK * dtSub; // dimensionless friction decrement per substep

  pPrev.set(p);
  uPrev.set(u);

  // Interior nodes: both characteristics arrive from the neighbors.
  //   C+ from j-1: R+ = (p' + Z·u')_{j-1} − Z·fk·u'_{j-1}
  //   C− from j+1: R− = (p' − Z·u')_{j+1} + Z·fk·u'_{j+1}
  for (let j = 1; j < N; j++) {
    const Rp = pPrev[j - 1] + Z * uPrev[j - 1] - Z * fk * uPrev[j - 1];
    const Rm = pPrev[j + 1] - Z * uPrev[j + 1] + Z * fk * uPrev[j + 1];
    p[j] = 0.5 * (Rp + Rm);
    u[j] = 0.5 * (Rp - Rm) / Z;
  }

  const tNow = state.time; // BC activation evaluated at the substep start

  // Left boundary (node 0): only C− arrives, from node 1.
  {
    const Rm = pPrev[1] - Z * uPrev[1] + Z * fk * uPrev[1];
    const b = state.left;
    if (b.kind === 'reservoir') {
      p[0] = 0;                       // pressure node → sign-flip reflection
      u[0] = -Rm / Z;
    } else if (b.kind === 'closed' && tNow >= b.closeTime) {
      u[0] = b.uTarget - b.uBase;     // velocity node (flow arrest) → Joukowsky source
      p[0] = Rm + Z * u[0];
    } else {
      p[0] = 0.5 * Rm;                // absorbing: external R+ = 0
      u[0] = -0.5 * Rm / Z;
    }
  }

  // Right boundary (node N): only C+ arrives, from node N−1.
  {
    const Rp = pPrev[N - 1] + Z * uPrev[N - 1] - Z * fk * uPrev[N - 1];
    const b = state.right;
    if (b.kind === 'reservoir') {
      p[N] = 0;                       // pressure node → sign-flip reflection
      u[N] = Rp / Z;
    } else if (b.kind === 'closed' && tNow >= b.closeTime) {
      u[N] = b.uTarget - b.uBase;     // velocity node (flow arrest) → Joukowsky source
      p[N] = Rp - Z * u[N];
    } else {
      p[N] = 0.5 * Rp;                // absorbing: external R− = 0
      u[N] = 0.5 * Rp / Z;
    }
  }

  state.time += dtSub;
}

// ---------------------------------------------------------------------------
// Application to the FV pipe state
// ---------------------------------------------------------------------------

/**
 * Apply the current perturbation field to the pipe incrementally:
 * cell pressure gets the mean of its two node p' values, face velocity/mdot
 * get the node u'. appliedP/appliedU record what is on the pipe so repeated
 * calls are idempotent and removeAcousticPerturbations restores the base
 * exactly (including when the total-pressure floor engages).
 */
function applyPerturbationsToPipe(state: AcousticState, pipe: PipeSegment): void {
  const { p, u, appliedP, appliedU } = state;
  const { nCells, area } = pipe;

  for (let i = 0; i < nCells; i++) {
    const target = 0.5 * (p[i] + p[i + 1]);
    let delta = target - appliedP[i];
    // Floor the TOTAL pressure (cavitation is not modeled — see header).
    if (pipe.cells[i].P + delta < MIN_TOTAL_PRESSURE_PA) {
      delta = MIN_TOTAL_PRESSURE_PA - pipe.cells[i].P;
    }
    pipe.cells[i].P += delta;
    appliedP[i] += delta;
  }

  for (let j = 0; j <= nCells; j++) {
    const delta = u[j] - appliedU[j];
    if (delta === 0) continue;
    const rho_f = j === 0 ? pipe.cells[0].rho :
                  j === nCells ? pipe.cells[nCells - 1].rho :
                  0.5 * (pipe.cells[j - 1].rho + pipe.cells[j].rho);
    pipe.faces[j].u += delta;
    pipe.faces[j].mdot += rho_f * area * delta;
    appliedU[j] += delta;
  }
}

/**
 * Remove the currently applied acoustic perturbation from the pipe,
 * restoring the base FV state (use before a SIMPLE pass re-solves the base
 * flow; re-apply by simply calling acousticStep again — it re-adds the full
 * standing perturbation). The acoustic field itself is untouched.
 */
export function removeAcousticPerturbations(state: AcousticState, pipe: PipeSegment): void {
  const { appliedP, appliedU } = state;
  const { nCells, area } = pipe;

  for (let i = 0; i < nCells; i++) {
    pipe.cells[i].P -= appliedP[i];
    appliedP[i] = 0;
  }
  for (let j = 0; j <= nCells; j++) {
    if (appliedU[j] === 0) continue;
    const rho_f = j === 0 ? pipe.cells[0].rho :
                  j === nCells ? pipe.cells[nCells - 1].rho :
                  0.5 * (pipe.cells[j - 1].rho + pipe.cells[j].rho);
    pipe.faces[j].u -= appliedU[j];
    pipe.faces[j].mdot -= rho_f * area * appliedU[j];
    appliedU[j] = 0;
  }
}

// ---------------------------------------------------------------------------
// Diagnostics
// ---------------------------------------------------------------------------

/**
 * Total acoustic energy of the perturbation field [J]:
 *   E = Σ_j w_j · [ p'²/(2ρc²) + ρ·u'²/2 ] · A·dx     (trapezoidal, w = ½ at ends)
 * Frictionless runs with reflective ends conserve E (the CFL = 1 scheme is a
 * pure invariant permutation).
 */
export function acousticEnergy(state: AcousticState, pipe: PipeSegment): number {
  const { p, u, rho, c, nNodes, dx } = state;
  const K = rho * c * c; // acoustic bulk modulus [Pa]
  let E = 0;
  for (let j = 0; j < nNodes; j++) {
    const w = (j === 0 || j === nNodes - 1) ? 0.5 : 1;
    E += w * (p[j] * p[j] / (2 * K) + 0.5 * rho * u[j] * u[j]);
  }
  return E * pipe.area * dx;
}

/**
 * Joukowsky equation — theoretical pressure rise from instantaneous flow stoppage.
 *
 * ΔP = ρ · c · ΔV
 *
 * @param rho Fluid density [kg/m³]
 * @param c Speed of sound [m/s]
 * @param deltaV Velocity change [m/s] (positive = flow reduction)
 * @returns Pressure rise [Pa]
 */
export function joukowskyPressureRise(rho: number, c: number, deltaV: number): number {
  return rho * c * Math.abs(deltaV);
}

/**
 * Wave propagation time — time for pressure wave to travel pipe length and return.
 *
 * t_return = 2L / c
 *
 * @param L Pipe length [m]
 * @param c Speed of sound [m/s]
 * @returns Return time [s]
 */
export function wavePropagationTime(L: number, c: number): number {
  return 2 * L / c;
}
